import dayjs from "dayjs";
import { useEffect } from "react";
import { rangePickerInitialValue } from "../../../../BookingForm/components/utils";
import { buildDepositDestinationFromInvoice } from "../depositDestinationOptions";

export const useInitializePaymentData = ({
  data,
  onEditBack,
  setValue,
  setPaymentStatusOn,
  setPaymentMode,
  setBankNameFromDropDown,
  bouncedChequeFee,
  visibilityOptions,
  paymentModeOptions,
  bouncedReasons,
  formatString,
  bounceVat,
}) => {
  useEffect(() => {
    if (!data) return;
    const chequeData = data?.params?.data;
    const isReceiptBounceFlow = data?.selectedOption === "mark_as_bounced" && Boolean(chequeData?.paymentReceiptId);

    // --- Step 1: Update cheque status based on selectedOption
    switch (data?.selectedOption) {
      case "confirm_payment":
        data.params.data.chequeStatus = "paid";
        break;
      case "transfer_in_progress":
        data.params.data.chequeStatus = "paid";
        break;
      case "mark_as_bounced":
        data.params.data.chequeStatus = "bounce";
        break;
      case "cancelled":
        data.params.data.chequeStatus = "cancelled";
        break;
      case "replace_cheque":
        data.params.data.chequeStatus = "pending";
        break;
      default:
        break;
    }

    const normalizedModeOfPayment = chequeData?.modeOfPayment || chequeData?.paymentMode;
    const normalizedDocumentNo =
      chequeData?.documentNo ||
      chequeData?.paymentRef ||
      chequeData?.paymentDetails?.chequeNo ||
      chequeData?.paymentDetails?.documentNo;
    const normalizedBankName = chequeData?.bankName || chequeData?.paymentDetails?.bankName;
    const normalizedChequeDate = chequeData?.chequeDate || chequeData?.paymentDetails?.chequeDate;
    const receiptBankName = chequeData?.paymentDetails?.bankName;

    // Invoice-only: when user opens Receive Payment on a row that already had
    // a prior receipt attempt (paid partial, paid+bounced, paid+recalled, etc.),
    // do not prefill payment-method/ref/bank from the prior receipt details.
    // Defaults from lease/owner (deposit destination, balance, full paid type)
    // are still set; only the fields the user enters each time are cleared.
    const hasPriorSubPdcReceipt = Array.isArray(chequeData?.subPdcs)
      ? chequeData.subPdcs.some((pdc: any) => pdc?.status?.toLowerCase() === "paid" && pdc?.paymentReceiptId)
      : false;
    const hasPriorPaymentReceipt = Array.isArray(chequeData?.paymentReceipts) && chequeData.paymentReceipts.length > 0;
    const hasPriorReceiptOnRow = Boolean(chequeData?.receiptNo);
    const skipPriorPaymentPrefill =
      data?.selectedOption === "confirm_payment" &&
      (hasPriorSubPdcReceipt || hasPriorPaymentReceipt || hasPriorReceiptOnRow);

    setPaymentStatusOn(chequeData?.chequeStatus);
    setValue("paymentMethodType", { label: "Full", value: 1 }, { shouldValidate: true });

    if (chequeData?.balance) {
      setValue("amountToPay", chequeData?.balance, { shouldValidate: false });
    }

    if (!chequeData?.balance) {
      setValue("amountToPay", chequeData?.totalAmount, { shouldValidate: false });
    }

    setValue("totalBalance", chequeData?.totalAmount || 0, { shouldValidate: false });

    const depositDestination = buildDepositDestinationFromInvoice(chequeData);
    if (depositDestination) {
      setValue("depositAccount", depositDestination.accountNumber, { shouldValidate: false });
      setValue("depositBank", depositDestination.bankName, { shouldValidate: false });
      setValue("depositTo", depositDestination, { shouldValidate: false });
    } else {
      setValue(
        "depositAccount",
        chequeData?.depositToAccountNumber || chequeData?.beneficiaryAccount || chequeData?.lessorAccountNumber || "",
        { shouldValidate: false },
      );
      setValue("depositBank", "", { shouldValidate: false });
      setValue(
        "depositTo",
        chequeData?.depositTo
          ? {
              label: chequeData.depositTo,
              value: chequeData.depositTo,
              bankName: "",
              accountNumber: "",
              depositToName: chequeData.depositTo,
            }
          : { value: "", label: "", bankName: "", accountNumber: "", depositToName: "" },
        { shouldValidate: false },
      );
    }

    setValue(
      "paymentStatus",
      {
        value: chequeData?.chequeStatus,
        label: visibilityOptions.find(
          (option) => option.value?.toLowerCase() === chequeData?.chequeStatus?.toLowerCase(),
        )?.label,
      },
      { shouldValidate: true },
    );

    if (skipPriorPaymentPrefill) {
      setValue("paymentModeType", null, { shouldValidate: false });
    } else {
      setValue(
        "paymentModeType",
        {
          value: normalizedModeOfPayment?.toLowerCase(),
          label: paymentModeOptions.find(
            (option) =>
              formatString(option.value)?.toLowerCase() === formatString(normalizedModeOfPayment)?.toLowerCase(),
          )?.label,
        },
        { shouldValidate: true },
      );
    }

    const bouncedPaymentModeSource = normalizedModeOfPayment;
    setValue(
      "bouncedChequePaymentMode",
      bouncedPaymentModeSource
        ? {
            value: bouncedPaymentModeSource?.toLowerCase(),
            label: paymentModeOptions.find(
              (option) =>
                formatString(option.value)?.toLowerCase() === formatString(bouncedPaymentModeSource)?.toLowerCase(),
            )?.label,
          }
        : null,
      { shouldValidate: true },
    );

    setPaymentMode(skipPriorPaymentPrefill ? null : normalizedModeOfPayment?.toLowerCase());
    const isReplaceCheque = data?.selectedOption === "replace_cheque" || chequeData?.bounceToPending;
    setValue(
      "chequeDate",
      !isReplaceCheque || skipPriorPaymentPrefill || !normalizedChequeDate ? null : dayjs(normalizedChequeDate),
      { shouldValidate: false },
    );
    setValue("paymentRef", skipPriorPaymentPrefill ? "" : normalizedDocumentNo, { shouldValidate: false });
    setValue("bouncedChequeFee", bouncedChequeFee, {
      shouldValidate: false,
    });
    setValue("vat", bounceVat, { shouldValidate: false });

    if (isReceiptBounceFlow && receiptBankName) {
      const receiptBankValue = receiptBankName.toLowerCase().replace(/\s+/g, "_");
      setValue("bankName", { value: receiptBankValue, label: receiptBankName }, { shouldValidate: false });
      setBankNameFromDropDown(receiptBankValue);
    } else if (normalizedBankName && !skipPriorPaymentPrefill) {
      const bank = formatString(normalizedBankName)?.toLowerCase();
      setValue("bankName", { value: bank, label: bank }, { shouldValidate: false });
      setBankNameFromDropDown(bank);
    } else if (skipPriorPaymentPrefill) {
      setValue("bankName", null, { shouldValidate: false });
      setBankNameFromDropDown(null);
    }

    if (chequeData?.subPdcs?.length > 0) {
      let partialPaidCheque = data?.params?.data?.subPdcs?.filter(
        (item) => item?.status?.toLowerCase() === "paid" && item?.paymentMode?.toLowerCase() === "cheque",
      );
      const lastSubPdc = partialPaidCheque[partialPaidCheque.length - 1];
      setValue("subPdcRef", { value: lastSubPdc?.subPdcId, label: lastSubPdc?.documentNo }, { shouldValidate: false });
    }

    setValue("paymentRefReplaced", skipPriorPaymentPrefill ? "" : normalizedDocumentNo);
    setValue("invoicePeriod", rangePickerInitialValue(data, "invice_period"));
    // --- Step 3: Handle reason field
    const reasonOption = bouncedReasons.find((opt) => opt.value === chequeData?.reason);
    setValue("reason", reasonOption ? { value: reasonOption?.value, label: reasonOption?.label } : null, {
      shouldValidate: false,
    });
  }, [data, onEditBack]);
};
